import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useGetMeQuery, useLogoutMutation } from '@/store/api';
import { Navigation } from '../Navigation/Navigation';

type AppLayoutProps = {
  children: ReactNode;
};

export const AppLayout = ({ children }: AppLayoutProps) => {
  const [logout, { isLoading }] = useLogoutMutation();
  const { data: user } = useGetMeQuery();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout().unwrap(); // triggers onQueryStarted side-effects
      navigate('/login', { replace: true });
    } catch {
      // Optionally show an error toast – but user is already "local-logged-out"
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className='min-h-screen bg-slate-900'>
      {/* Header */}
      <header className='fixed top-0 left-0 right-0 bg-slate-800 border-b border-slate-700 px-4 md:px-6 py-4 z-30'>
        <div className='flex justify-end items-center md:justify-end'>
          <div className='flex items-center gap-2 md:gap-4'>
            <span className='text-slate-300 hidden sm:inline text-sm md:text-base'>
              Welcome, {user?._doc?.firstName ?? 'Admin User'}
            </span>
            <span className='text-slate-300 sm:hidden text-sm'>Hi, {user?._doc?.firstName ?? 'User'}</span>
            <Button
              onClick={handleLogout}
              variant='outline'
              size='sm'
              className='gap-1 md:gap-2 h-8 md:h-9 bg-[#f9f9f9]'
              style={{ backgroundColor: '#f9f9f9', color: '#1a202c' }}
              disabled={isLoading}
            >
              <LogOut className='h-4 w-4' />
              <span className='hidden sm:inline text-sm'>{isLoading ? 'Logging out...' : 'Logout'}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Sidebar */}
      <Navigation />

      {/* Main Content */}
      <main className='pt-14 md:pl-16 transition-all duration-300 min-h-screen'>
        <div className='p-6 md:p-8 lg:p-10'>{children}</div>
      </main>
    </div>
  );
};
